import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, LogIn, Users, Sparkles } from "lucide-react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function ClassicLanding({ config, navigate, user }) {
  return (
    <div className="landing-hero" data-testid="landing-classic">
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 landing-image" style={{ backgroundImage: `url(${config.image_url})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0f0f1a]" />
      </div>
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center lg:items-start px-8 sm:px-12 lg:px-16 py-12 relative bg-[#0f0f1a]">
        <div className="lg:hidden absolute inset-0 landing-image opacity-30" style={{ backgroundImage: `url(${config.image_url})` }} />
        <div className="lg:hidden absolute inset-0 bg-[#0f0f1a]/80" />
        <NavButtons user={user} navigate={navigate} />
        <div className="relative z-10 max-w-lg animate-fade-up">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#f0ece2] mb-6 leading-tight" data-testid="landing-title">{config.title}</h1>
          <p className="text-base sm:text-lg text-[#a0a0b8] mb-10 leading-relaxed" data-testid="landing-subtitle">{config.subtitle}</p>
          <Button data-testid="reserve-cta-btn" size="lg" className="bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold text-base px-8 py-6 rounded-lg shadow-lg shadow-[#c9a84c]/20 transition-all hover:-translate-y-0.5" onClick={() => navigate("/reservar")}>
            <Calendar className="w-5 h-5 mr-2" /> {config.cta_text}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModernLanding({ config, navigate, user }) {
  return (
    <div className="relative min-h-screen flex items-center justify-center" data-testid="landing-modern">
      <div className="absolute inset-0 landing-image" style={{ backgroundImage: `url(${config.image_url})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f1a]/70 via-[#0f0f1a]/50 to-[#0f0f1a]/90" />
      <div className="absolute top-0 left-0 right-0 z-20 flex justify-end p-6">
        {user ? (
          <Button data-testid="dashboard-btn" variant="outline" className="border-[#c9a84c]/50 text-[#c9a84c] bg-[#0f0f1a]/40 backdrop-blur-sm hover:bg-[#c9a84c]/10" onClick={() => navigate(user.role === 'admin' ? '/admin' : user.role === 'sede' ? '/sede' : '/mis-reservas')}>
            <Users className="w-4 h-4 mr-2" /> Mi Panel
          </Button>
        ) : (
          <Button data-testid="login-btn" variant="outline" className="border-[#c9a84c]/50 text-[#c9a84c] bg-[#0f0f1a]/40 backdrop-blur-sm hover:bg-[#c9a84c]/10" onClick={() => navigate("/acceder")}>
            <LogIn className="w-4 h-4 mr-2" /> Acceder
          </Button>
        )}
      </div>
      <div className="relative z-10 text-center max-w-2xl px-6 animate-fade-up">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a84c]/10 border border-[#c9a84c]/30 backdrop-blur-sm mb-8">
          <Sparkles className="w-4 h-4 text-[#c9a84c]" />
          <span className="text-sm text-[#c9a84c] font-medium">Reserva tu experiencia</span>
        </div>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#f0ece2] mb-6 leading-tight" data-testid="landing-title">{config.title}</h1>
        <p className="text-lg sm:text-xl text-[#a0a0b8] mb-12 leading-relaxed max-w-xl mx-auto" data-testid="landing-subtitle">{config.subtitle}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button data-testid="reserve-cta-btn" size="lg" className="bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold text-lg px-10 py-7 rounded-xl shadow-2xl shadow-[#c9a84c]/30 transition-all hover:-translate-y-1" onClick={() => navigate("/reservar")}>
            <Calendar className="w-5 h-5 mr-2" /> {config.cta_text}
          </Button>
        </div>
      </div>
    </div>
  );
}

function NavButtons({ user, navigate }) {
  return (
    <div className="relative z-10 w-full flex justify-end mb-12 gap-3">
      {user ? (
        <Button data-testid="dashboard-btn" variant="outline" className="border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10" onClick={() => navigate(user.role === 'admin' ? '/admin' : user.role === 'sede' ? '/sede' : '/mis-reservas')}>
          <Users className="w-4 h-4 mr-2" /> Mi Panel
        </Button>
      ) : (
        <Button data-testid="login-btn" variant="outline" className="border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10" onClick={() => navigate("/acceder")}>
          <LogIn className="w-4 h-4 mr-2" /> Acceder
        </Button>
      )}
    </div>
  );
}

export default function LandingPage() {
  const [config, setConfig] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    API.get("/landing").then(r => setConfig(r.data)).catch(() => {});
  }, []);

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f1a]">
        <div className="text-[#a0a0b8]">Cargando...</div>
      </div>
    );
  }

  return (
    <div data-testid="landing-page">
      {config.template === "classic" ? (
        <ModernLanding config={config} navigate={navigate} user={user} />
      ) : (
        <ClassicLanding config={config} navigate={navigate} user={user} />
      )}
    </div>
  );
}
